import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { HttpStatusCode } from 'axios';

export type AppViolationsResponse = {
    [key: string]: string;
};

export type Error = {
    code: number;
    message: string;
    violations: AppViolationsResponse;
};

export type ErrorResponse = {
    error: Error;
};

export type GeneralErrorResponse = {
    error: Omit<Error, 'violations'>;
};

export const isValidationErrorResponse = (
    error: FetchBaseQueryError | SerializedError,
): error is { status: 400; data: ErrorResponse } => {
    if (!('status' in error)) {
        return false;
    }

    const data = error.data as ErrorResponse;

    return (
        data &&
        data.error &&
        data.error.code === 400 &&
        data.error.message !== undefined &&
        data.error.violations !== undefined
    );
};

export const isGeneralErrorResponse = (
    error: FetchBaseQueryError | SerializedError,
): error is { status: 400; data: GeneralErrorResponse } => {
    if (!('status' in error)) {
        return false;
    }

    const data = error.data as GeneralErrorResponse;

    if (!('error' in data)) {
        return false;
    }

    return data.error.code === 400 && data.error.message !== undefined;
};

export const isPaymentRequiredErrorResponse = (
    error: FetchBaseQueryError | SerializedError,
): error is { status: HttpStatusCode.PaymentRequired; data: GeneralErrorResponse } => {
    if (!('status' in error)) {
        return false;
    }

    const data = error.data as GeneralErrorResponse;

    if (!('error' in data)) {
        return false;
    }

    return data.error.code === HttpStatusCode.PaymentRequired && data.error.message !== undefined;
};

export const constructUrl = (
    url: string,
    searchParams: Record<string, string | string[] | number | number[] | boolean | undefined>,
) => {
    const urlWithParams = new URL(url, document.baseURI);
    Object.entries(searchParams).forEach(([key, value]) => {
        if (typeof value !== 'undefined' && value !== '' && value !== null) {
            if (Array.isArray(value)) {
                // just in case, clear existing values before appending all new ones
                urlWithParams.searchParams.delete(key);
                value.forEach((val) => urlWithParams.searchParams.append(`${key}[]`, val.toString()));
            } else {
                urlWithParams.searchParams.set(
                    key,
                    typeof value === 'boolean' || typeof value === 'number' ? JSON.stringify(value) : value,
                );
            }
        }
    });

    return urlWithParams.toString();
};

export const mapObjectToFormData = <T extends object>(object: T, namespace?: string, form?: FormData) => {
    const formData = form || new FormData();

    Object.entries(object).forEach(([property, value]) => {
        const formKey = namespace ? `${namespace}[${property}]` : property;
        if (value instanceof Date) {
            formData.append(formKey, value.toISOString());
        } else if (value !== null && typeof value === 'object' && !(value instanceof File)) {
            mapObjectToFormData(value, formKey, formData);
        } else if (typeof value === 'boolean') {
            formData.append(formKey, String(value ? 1 : 0));
        } else if (typeof value !== 'undefined') {
            formData.append(formKey, String(value));
        }
    });

    return formData;
};
